import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { EsraPipingAPIClient, ServiceViewModel } from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

@Component({
    selector: 'app-service-add-edit',
    templateUrl: './service-add-edit.component.html',
    styleUrls: ['./service-add-edit.component.scss'],
    standalone: false
})
export class ServiceAddEditComponent implements OnInit {
  inputFormGroup: UntypedFormGroup;
  serviceToUpdate: ServiceViewModel | null = null;

  title: string = '';
  genericMissingValueMessage: string = 'This is a required field';

  isEditMode: boolean = false;
  isCreateMode: boolean = false;
  submitClicked: boolean = false;
  saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly dialogRef: MatDialogRef<ServiceAddEditComponent, boolean>,
    private readonly esraApiClient: EsraPipingAPIClient,
    private readonly snackBarService: SnackBarService,
    private readonly formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) private readonly service: ServiceViewModel | null
  ) {
    this.inputFormGroup = this.formBuilder.group({
      name: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.serviceToUpdate = this.service ? new ServiceViewModel(this.service) : null;
    this.isEditMode = this.serviceToUpdate !== null;
    this.isCreateMode = !this.isEditMode;

    if (this.isCreateMode) {
      this.title = 'Create a new service';
    }

    if (this.serviceToUpdate) {
      this.title = `Edit ${this.serviceToUpdate.name}`;
      this.setFormValues();
    }
  }

  setFormValues(): void {
    if (!this.serviceToUpdate) {
      return;
    }

    this.inputFormGroup.setValue({
      name: this.serviceToUpdate.name
    });
  }

  save(): void {
    this.submitClicked = true;

    if (this.inputFormGroup.invalid) {
      this.inputFormGroup.markAllAsTouched();
      return;
    }

    const newService: ServiceViewModel = new ServiceViewModel({
      name: this.inputFormGroup.controls['name'].value,
      isActive: true
    });

    if (this.isCreateMode) {
      this.esraApiClient.createService(newService).pipe(
        indicate(this.saving$),
        first()
      ).subscribe({
        next: () => {
          this.snackBarService.showSnackBar(true, `The new service, ${newService.name}, was successfully created.`);
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.snackBarService.showSnackBar(true, err.response, 'mat-snack-bar-error');
        }
      });
    }

    const serviceToUpdate: ServiceViewModel | null = this.serviceToUpdate;
    if (this.isEditMode && serviceToUpdate) {
      serviceToUpdate.name = newService.name;
      this.esraApiClient.updateService(serviceToUpdate).pipe(
        indicate(this.saving$),
        first()
      ).subscribe({
        next: () => {
          this.snackBarService.showSnackBar(true, `Service name was changed to ${serviceToUpdate.name}`);
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.snackBarService.showSnackBar(true, err.response, 'mat-snack-bar-error');
        }
      });
    }
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }
}
